import axios, { AxiosResponse } from 'axios';
import type {
	ApiError,
	BulkMoveRequest,
	BulkMoveResponse,
	CreateFlowRequest,
	CreateFolderRequest,
	CreateNestedFolderRequest,
	EditSubmissionRequest,
	Execution,
	ExecutionDetail,
	Flow,
	Folder,
	FormSchemaResponse,
	FormSubmissionRequest,
	MoveFolderRequest,
	PaginatedResponse,
	RetryExecutionRequest,
	UpdateFlowRequest,
	UpdateFolderRequest,
} from '../types/api';
import type {
	ExecutionPaginationParams,
	FlowPaginationParams,
	FolderPaginationParams,
} from '../types/pagination';

/**
 * API Configuration Constants
 */
const API_TIMEOUT_MS = 30000; // 30 seconds - adjust this value to change timeout for all API requests

/**
 * Base API URL - use environment-specific configuration
 */
const getApiBaseUrl = () => {
	// Use injected environment variables from vite.config.ts
	const apiUrl = process.env.VITE_API_URL || import.meta.env.VITE_API_URL;

	if (import.meta.env.DEV) {
		// In development mode, use Vite proxy
		return '/api/v1';
	}

	// In production or other environments, use the full API URL
	return `${apiUrl}/api/v1` || 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Environment configuration utilities
 */
export const envConfig = {
	get apiUrl() {
		return process.env.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3000';
	},
	get environment() {
		return process.env.VITE_ENV || import.meta.env.VITE_ENV || 'local';
	},
	get isFalsoEnabled() {
		const enabled =
			process.env.VITE_FALSO_ENABLED || import.meta.env.VITE_FALSO_ENABLED || 'false';
		return enabled.toLowerCase() === 'true';
	},
	get googleClientId() {
		return process.env.VITE_GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID;
	},
	get isDevelopment() {
		return import.meta.env.DEV;
	},
	get isProduction() {
		return import.meta.env.PROD;
	},
};

/**
 * Get authentication token from storage
 */
const getAuthToken = (): string | null => {
	return sessionStorage.getItem('auth_token');
};

/**
 * Clear authentication data
 */
const clearAuthData = (): void => {
	sessionStorage.removeItem('auth_token');
	sessionStorage.removeItem('auth_user');
};

/**
 * Axios instance with base configuration for composer endpoints
 */
const apiClient = axios.create({
	baseURL: `${API_BASE_URL}/composer`,
	timeout: API_TIMEOUT_MS,
	headers: {
		'Content-Type': 'application/json',
	},
	withCredentials: true, // Include cookies for refresh token
});

/**
 * Axios instance for form endpoints (standalone, not under /composer)
 */
const formApiClient = axios.create({
	baseURL: API_BASE_URL,
	timeout: API_TIMEOUT_MS,
	headers: {
		'Content-Type': 'application/json',
	},
	withCredentials: true, // Include cookies for refresh token
});

/**
 * Axios instance for validation endpoints (under composer module)
 */
const validationApiClient = axios.create({
	baseURL: `${API_BASE_URL}/composer`,
	timeout: API_TIMEOUT_MS,
	headers: {
		'Content-Type': 'application/json',
	},
	withCredentials: true, // Include cookies for refresh token
});

/**
 * Request interceptor to add authentication headers
 */
const requestInterceptor = (config: any) => {
	const token = getAuthToken();
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	return config;
};

/**
 * Track refresh requests to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

/**
 * Add a subscriber to wait for token refresh
 */
const subscribeTokenRefresh = (cb: (token: string | null) => void) => {
	refreshSubscribers.push(cb);
};

/**
 * Notify all subscribers when refresh is complete
 */
const onTokenRefreshed = (token: string | null) => {
	refreshSubscribers.forEach(cb => cb(token));
	refreshSubscribers = [];
};

/**
 * Attempt to refresh the access token
 */
const refreshToken = async (): Promise<string | null> => {
	try {
		const response = await fetch(
			`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/v1/auth/refresh`,
			{
				method: 'POST',
				credentials: 'include', // Include refresh token cookie
			}
		);

		if (!response.ok) {
			throw new Error('Token refresh failed');
		}

		const data = await response.json();
		const newToken = data.accessToken;

		// Store new token
		sessionStorage.setItem('auth_token', newToken);

		return newToken;
	} catch (error) {
		console.error('Token refresh failed:', error);
		clearAuthData();
		// Redirect to login page
		window.location.href = '/login';
		return null;
	}
};

/**
 * Response interceptor for error handling and token refresh
 */
const responseInterceptor = (response: any) => response;

const errorInterceptor = async (error: any) => {
	const originalRequest = error.config;

	// Handle 401 Unauthorized errors with automatic token refresh
	if (error.response?.status === 401 && !originalRequest._retry) {
		if (isRefreshing) {
			// If already refreshing, wait for the result
			return new Promise((resolve, reject) => {
				subscribeTokenRefresh((token: string | null) => {
					if (token) {
						originalRequest.headers.Authorization = `Bearer ${token}`;
						originalRequest._retry = true;
						resolve(axios(originalRequest));
					} else {
						reject(error);
					}
				});
			});
		}

		originalRequest._retry = true;
		isRefreshing = true;

		try {
			const newToken = await refreshToken();

			if (newToken) {
				// Update the original request with new token
				originalRequest.headers.Authorization = `Bearer ${newToken}`;
				onTokenRefreshed(newToken);

				// Retry the original request
				return axios(originalRequest);
			} else {
				onTokenRefreshed(null);
				throw error;
			}
		} catch (refreshError) {
			onTokenRefreshed(null);
			throw error;
		} finally {
			isRefreshing = false;
		}
	}

	// Handle other errors
	if (error.response?.data) {
		throw error.response.data as ApiError;
	}
	throw {
		message: error.message || 'An unexpected error occurred',
		statusCode: error.response?.status || 500,
	} as ApiError;
};

// Apply interceptors to all API clients
[apiClient, formApiClient, validationApiClient].forEach(client => {
	client.interceptors.request.use(requestInterceptor);
	client.interceptors.response.use(responseInterceptor, errorInterceptor);
});

/**
 * API service for flow/workflow management
 */
export const flowsApi = {
	/**
	 * Get all flows with optional filtering and pagination
	 */
	async getFlows(params?: FlowPaginationParams): Promise<PaginatedResponse<Flow>> {
		// Build query parameters
		const searchParams = new URLSearchParams();

		// Standard pagination params
		if (params?.page) searchParams.set('page', params.page.toString());
		if (params?.limit) searchParams.set('limit', params.limit.toString());
		if (params?.search) searchParams.set('search', params.search);

		// Flow-specific filters
		if (params?.folderId) searchParams.set('folderId', params.folderId);
		if (params?.status) searchParams.set('status', params.status);
		if (params?.isPublic !== undefined)
			searchParams.set('isPublic', params.isPublic.toString());
		if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
		if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

		// Handle tags array
		if (params?.tags && params.tags.length > 0) {
			params.tags.forEach(tag => searchParams.append('tags', tag));
		}

		const url = searchParams.toString() ? `/flows?${searchParams}` : '/flows';
		const response: AxiosResponse<PaginatedResponse<Flow> | Flow[]> = await apiClient.get(url);

		// Handle backward compatibility: if backend returns array, transform to paginated format
		if (Array.isArray(response.data)) {
			const flows = response.data as Flow[];
			const page = params?.page || 1;
			const limit = params?.limit || 20;
			const startIndex = (page - 1) * limit;
			const endIndex = startIndex + limit;
			const paginatedFlows = flows.slice(startIndex, endIndex);

			return {
				data: paginatedFlows,
				pagination: {
					page,
					limit,
					total: flows.length,
					pages: Math.ceil(flows.length / limit),
					hasPrevious: page > 1,
					hasNext: endIndex < flows.length,
				},
			};
		}

		// Return new paginated format as-is
		return response.data as PaginatedResponse<Flow>;
	},

	/**
	 * Get a specific flow by ID
	 */
	async getFlow(id: string): Promise<Flow> {
		const response: AxiosResponse<Flow> = await apiClient.get(`/flows/${id}`);
		return response.data;
	},

	/**
	 * Get all available tags
	 */
	async getTags(): Promise<string[]> {
		const response: AxiosResponse<string[]> = await apiClient.get('/flows/tags');
		return response.data;
	},

	/**
	 * Create a new flow
	 */
	async createFlow(data: CreateFlowRequest): Promise<Flow> {
		const response: AxiosResponse<Flow> = await apiClient.post('/flows', data);
		return response.data;
	},

	/**
	 * Update an existing flow
	 */
	async updateFlow(id: string, data: UpdateFlowRequest): Promise<Flow> {
		const response: AxiosResponse<Flow> = await apiClient.patch(`/flows/${id}`, data);
		return response.data;
	},

	/**
	 * Delete a flow
	 */
	async deleteFlow(id: string): Promise<void> {
		await apiClient.delete(`/flows/${id}`);
	},

	/**
	 * Publish a flow
	 */
	async publishFlow(id: string): Promise<Flow> {
		const response: AxiosResponse<Flow> = await apiClient.post(`/flows/${id}/publish`);
		return response.data;
	},

	/**
	 * Unpublish a flow
	 */
	async unpublishFlow(id: string): Promise<Flow> {
		const response: AxiosResponse<Flow> = await apiClient.post(`/flows/${id}/unpublish`);
		return response.data;
	},

	/**
	 * Clone/duplicate a flow
	 */
	async cloneFlow(id: string, name?: string): Promise<Flow> {
		const response: AxiosResponse<Flow> = await apiClient.post(`/flows/${id}/clone`, { name });
		return response.data;
	},
};

/**
 * API service for folder management
 */
export const foldersApi = {
	/**
	 * Get all folders with optional pagination and filtering
	 */
	async getFolders(params?: FolderPaginationParams): Promise<PaginatedResponse<Folder>> {
		// Build query parameters
		const searchParams = new URLSearchParams();

		// Standard pagination params
		if (params?.page) searchParams.set('page', params.page.toString());
		if (params?.limit) searchParams.set('limit', params.limit.toString());
		if (params?.search) searchParams.set('search', params.search);

		// Folder-specific filters
		if (params?.includeFlowCount) searchParams.set('includeFlowCount', 'true');
		if (params?.includeFlows) searchParams.set('includeFlows', 'true');
		if (params?.emptyOnly) searchParams.set('emptyOnly', 'true');
		if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
		if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

		const url = searchParams.toString() ? `/folders?${searchParams}` : '/folders';
		const response: AxiosResponse<PaginatedResponse<Folder> | Folder[]> = await apiClient.get(
			url
		);

		// Handle backward compatibility: if backend returns array, transform to paginated format
		if (Array.isArray(response.data)) {
			const folders = response.data as Folder[];
			const page = params?.page || 1;
			const limit = params?.limit || 20;
			const startIndex = (page - 1) * limit;
			const endIndex = startIndex + limit;
			const paginatedFolders = folders.slice(startIndex, endIndex);

			return {
				data: paginatedFolders,
				pagination: {
					page,
					limit,
					total: folders.length,
					pages: Math.ceil(folders.length / limit),
					hasPrevious: page > 1,
					hasNext: endIndex < folders.length,
				},
			};
		}

		// Return new paginated format as-is
		return response.data as PaginatedResponse<Folder>;
	},

	/**
	 * Get a specific folder by ID
	 */
	async getFolder(id: string): Promise<Folder> {
		const response: AxiosResponse<Folder> = await apiClient.get(`/folders/${id}`);
		return response.data;
	},

	/**
	 * Create a new folder
	 */
	async createFolder(data: CreateFolderRequest): Promise<Folder> {
		const response: AxiosResponse<Folder> = await apiClient.post('/folders', data);
		return response.data;
	},

	/**
	 * Update an existing folder
	 */
	async updateFolder(id: string, data: UpdateFolderRequest): Promise<Folder> {
		const response: AxiosResponse<Folder> = await apiClient.patch(`/folders/${id}`, data);
		return response.data;
	},

	/**
	 * Delete a folder
	 */
	async deleteFolder(id: string): Promise<void> {
		await apiClient.delete(`/folders/${id}`);
	},

	// ============================================
	// HIERARCHICAL FOLDER METHODS
	// ============================================

	/**
	 * Get the complete folder tree structure
	 */
	async getFolderTree(): Promise<Folder[]> {
		const response: AxiosResponse<any[]> = await apiClient.get('/folders/tree');

		// Transform backend response to add flowCount
		const transformFolder = (folder: any): Folder => ({
			...folder,
			flowCount: folder.flows?.length || 0,
			children: folder.children?.map(transformFolder) || [],
		});

		return response.data.map(transformFolder);
	},

	/**
	 * Get all root folders (folders without parents)
	 */
	async getRootFolders(): Promise<Folder[]> {
		const response: AxiosResponse<any[]> = await apiClient.get('/folders/root');

		// Transform backend response to add flowCount
		const transformFolder = (folder: any): Folder => ({
			...folder,
			flowCount: folder.flows?.length || 0,
			children: folder.children?.map(transformFolder) || [],
		});

		return response.data.map(transformFolder);
	},

	/**
	 * Create a nested folder with parent relationship
	 */
	async createNestedFolder(data: CreateNestedFolderRequest): Promise<Folder> {
		const response: AxiosResponse<Folder> = await apiClient.post('/folders/nested', data);
		return response.data;
	},

	/**
	 * Get all children of a specific folder
	 */
	async getFolderChildren(parentId: string): Promise<Folder[]> {
		const response: AxiosResponse<any[]> = await apiClient.get(`/folders/${parentId}/children`);

		// Transform backend response to add flowCount
		const transformFolder = (folder: any): Folder => ({
			...folder,
			flowCount: folder.flows?.length || 0,
			children: folder.children?.map(transformFolder) || [],
		});

		return response.data.map(transformFolder);
	},

	/**
	 * Get all ancestors of a folder (for breadcrumbs)
	 */
	async getFolderAncestors(folderId: string): Promise<Folder[]> {
		const response: AxiosResponse<Folder[]> = await apiClient.get(
			`/folders/${folderId}/ancestors`
		);
		return response.data;
	},

	/**
	 * Move a folder to a new parent location
	 */
	async moveFolder(folderId: string, data: MoveFolderRequest): Promise<Folder> {
		const response: AxiosResponse<Folder> = await apiClient.post(
			`/folders/${folderId}/move`,
			data
		);
		return response.data;
	},

	/**
	 * Check if moving a folder would create a cycle
	 */
	async wouldCreateCycle(
		folderId: string,
		parentId: string
	): Promise<{ wouldCreateCycle: boolean }> {
		const response: AxiosResponse<{ wouldCreateCycle: boolean }> = await apiClient.get(
			`/folders/${folderId}/would-create-cycle/${parentId}`
		);
		return response.data;
	},

	/**
	 * Get enhanced folder statistics including nested folder counts
	 */
	async getFolderStats(folderId: string): Promise<{
		totalFlows: number;
		publishedFlows: number;
		draftFlows: number;
		totalExecutions: number;
		lastActivityAt: string | null;
		childFolders: number;
		totalDescendantFolders: number;
		depth: number;
		fullPath: string;
	}> {
		const response: AxiosResponse<any> = await apiClient.get(
			`/folders/${folderId}/nested-stats`
		);
		return response.data;
	},

	/**
	 * Bulk move operation for multiple folders and workflows
	 */
	async bulkMove(data: BulkMoveRequest): Promise<BulkMoveResponse> {
		const response: AxiosResponse<BulkMoveResponse> = await apiClient.post(
			'/folders/bulk-move',
			data
		);
		return response.data;
	},

	/**
	 * Delete a folder with enhanced options for children handling
	 */
	async deleteNestedFolder(folderId: string, moveChildrenToRoot: boolean = true): Promise<void> {
		const params = new URLSearchParams();
		params.set('moveChildrenToRoot', moveChildrenToRoot.toString());
		await apiClient.delete(`/folders/${folderId}/nested?${params}`);
	},
};

/**
 * Helper function to transform raw execution data to match Execution interface
 */
function transformExecution(execution: any): Execution {
	// Extract trigger data from various possible locations
	let submissionData = {};

	if (execution.contextData) {
		// Priority 1: Form submission data
		if (execution.contextData.submission) {
			submissionData = execution.contextData.submission;
		}
		// Priority 2: Webhook/HTTP endpoint data in contextData.data
		else if (execution.contextData.data) {
			submissionData = execution.contextData.data;
		}
		// Priority 3: Data stored under start node ID (webhook pattern)
		else {
			// Look for start.* node data in contextData
			const startNodeKey = Object.keys(execution.contextData).find(key =>
				key.includes('start.')
			);
			if (startNodeKey) {
				const startNodeData = execution.contextData[startNodeKey];
				// Extract webhookPayload or the entire node data
				submissionData = startNodeData?.webhookPayload || startNodeData || {};
			}
		}
	}

	// Fallback to legacy submissionData field
	if (Object.keys(submissionData).length === 0 && execution.submissionData) {
		submissionData = execution.submissionData;
	}

	return {
		id: execution.id,
		flowId: execution.flowId || execution.flow?.id || '',
		flowName: execution.flow?.name || 'Unknown Flow',
		flowVersion: execution.flow?.version?.toString() || '1',
		status: execution.status,
		startedAt: execution.startedAt || execution.createdAt || new Date().toISOString(),
		completedAt:
			execution.completedAt ||
			(execution.status === 'completed' ? execution.updatedAt : undefined),
		submissionData,
		error: execution.error?.message || execution.error,
	};
}

/**
 * API service for execution management
 */
export const executionsApi = {
	/**
	 * Get all executions with optional filtering and pagination
	 */
	async getExecutions(params?: ExecutionPaginationParams): Promise<PaginatedResponse<Execution>> {
		// Build query parameters
		const searchParams = new URLSearchParams();

		// Standard pagination params
		if (params?.page) searchParams.set('page', params.page.toString());
		if (params?.limit) searchParams.set('limit', params.limit.toString());
		if (params?.search) searchParams.set('search', params.search);

		// Execution-specific filters
		if (params?.status) searchParams.set('status', params.status);
		if (params?.flowId) searchParams.set('flowId', params.flowId);
		if (params?.submitterEmail) searchParams.set('submitterEmail', params.submitterEmail);
		if (params?.createdAfter) searchParams.set('createdAfter', params.createdAfter);
		if (params?.createdBefore) searchParams.set('createdBefore', params.createdBefore);
		if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
		if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

		const url = searchParams.toString() ? `/executions?${searchParams}` : '/executions';
		const response: AxiosResponse<PaginatedResponse<Execution> | any[]> = await apiClient.get(
			url
		);

		// Handle backward compatibility: if backend returns array, transform to paginated format
		if (Array.isArray(response.data)) {
			const executions = response.data.map(transformExecution);
			const page = params?.page || 1;
			const limit = params?.limit || 20;
			const startIndex = (page - 1) * limit;
			const endIndex = startIndex + limit;
			const paginatedExecutions = executions.slice(startIndex, endIndex);

			return {
				data: paginatedExecutions,
				pagination: {
					page,
					limit,
					total: executions.length,
					pages: Math.ceil(executions.length / limit),
					hasPrevious: page > 1,
					hasNext: endIndex < executions.length,
				},
			};
		}

		// Handle new paginated format with transformation
		const paginatedResponse = response.data as PaginatedResponse<any>;
		return {
			...paginatedResponse,
			data: paginatedResponse.data.map(transformExecution),
		};
	},

	/**
	 * Get detailed execution information by ID
	 */
	async getExecution(id: string): Promise<ExecutionDetail> {
		const response: AxiosResponse<any> = await apiClient.get(`/executions/${id}`);
		const execution = response.data;

		// Transform the execution response to match expected structure
		const transformedExecution = transformExecution(execution);

		// Use real logs and node statuses from backend
		const logs = execution.logs || [];
		const nodeStatuses = execution.nodeStatuses || {};

		return {
			...transformedExecution,
			logs,
			nodeStatuses,
			// Include flow data for trigger detection
			flow: execution.flow
				? {
						id: execution.flow.id,
						name: execution.flow.name,
						dagDefinition: execution.flow.dagDefinition || {
							nodes: [],
							edges: [],
						},
						submissionForm: execution.flow.submissionForm,
				  }
				: undefined,
		};
	},

	/**
	 * Retry a failed execution
	 */
	async retryExecution(id: string, data?: RetryExecutionRequest): Promise<Execution> {
		const response: AxiosResponse<any> = await apiClient.post(`/executions/${id}/retry`, data);
		return transformExecution(response.data);
	},

	/**
	 * Edit execution submission data and restart
	 */
	async editSubmissionData(id: string, data: EditSubmissionRequest): Promise<Execution> {
		const response: AxiosResponse<any> = await apiClient.patch(
			`/executions/${id}/edit-submission`,
			data
		);
		return transformExecution(response.data);
	},

	/**
	 * Cancel a running execution
	 */
	async cancelExecution(id: string): Promise<Execution> {
		const response: AxiosResponse<any> = await apiClient.patch(`/executions/${id}/cancel`);
		return transformExecution(response.data);
	},
};

/**
 * API service for form management
 */
export const formsApi = {
	/**
	 * Get form schema for a published workflow
	 */
	async getFormSchema(flowId: string): Promise<FormSchemaResponse> {
		const response: AxiosResponse<FormSchemaResponse> = await formApiClient.get(
			`/form/${flowId}`
		);
		return response.data;
	},

	/**
	 * Submit a form for a workflow
	 */
	async submitForm(flowId: string, formData: FormSubmissionRequest): Promise<any> {
		const response: AxiosResponse<any> = await formApiClient.post(
			`/form/${flowId}/submit`,
			formData
		);
		return response.data;
	},
};

/**
 * Webhook API service
 */
export const webhooksApi = {
	/**
	 * Test a webhook by sending a payload to the webhook endpoint
	 */
	testWebhook: async (
		webhookPath: string,
		payload: Record<string, any>,
		headers?: Record<string, string>
	): Promise<{
		success: boolean;
		executionId: string;
		flowId: string;
		message: string;
	}> => {
		const response = await axios.post(
			`${API_BASE_URL}/triggers/webhook/${webhookPath}`,
			payload,
			{
				headers: {
					'Content-Type': 'application/json',
					...headers,
				},
			}
		);
		return response.data;
	},
};

/**
 * Validation API types
 */
export interface ValidationError {
	field: string;
	message: string;
	code?: string;
	severity: 'error' | 'warning' | 'info';
	suggestion?: string;
	path?: string;
	context?: Record<string, any>;
}

export interface ValidationResult {
	valid: boolean;
	nodeId?: string;
	errors: ValidationError[];
	metadata?: {
		validationTime?: number;
		mode?: 'strict' | 'ai' | 'both';
		aiEnhanced?: boolean;
	};
}

export interface ValidateNodeRequest {
	nodeType: string;
	nodeData: Record<string, any>;
	dagDefinition?: Record<string, any>;
	mode?: 'strict' | 'ai' | 'both';
	nodeId?: string;
}

/**
 * Validation API service
 */
export const validationApi = {
	/**
	 * Validate a single node configuration
	 */
	async validateNode(request: ValidateNodeRequest): Promise<ValidationResult> {
		const DEBUG_VALIDATION = false; // Set to true only when debugging validation issues

		if (DEBUG_VALIDATION) {
			console.log('🌐 Validation API - Making request to:', '/validation/validate-node');
			console.log('🌐 Validation API - Base URL:', validationApiClient.defaults.baseURL);
			console.log(
				'🌐 Validation API - Full URL:',
				`${validationApiClient.defaults.baseURL}/validation/validate-node`
			);
			console.log('🌐 Validation API - Request payload:', request);
		}

		try {
			const response: AxiosResponse<ValidationResult> = await validationApiClient.post(
				'/validation/validate-node',
				request
			);

			if (DEBUG_VALIDATION) {
				console.log('✅ Validation API - Response received:', {
					status: response.status,
					statusText: response.statusText,
					headers: response.headers,
					data: response.data,
				});
			}

			return response.data;
		} catch (error: any) {
			if (DEBUG_VALIDATION) {
				console.error('❌ Validation API - Request failed:', {
					error: error.message,
					response: error.response?.data,
					status: error.response?.status,
					statusText: error.response?.statusText,
					headers: error.response?.headers,
				});
			}
			throw error;
		}
	},

	/**
	 * Validate a complete DAG structure
	 */
	async validateDAG(request: {
		nodes: any[];
		edges: any[];
		mode?: 'strict' | 'ai' | 'both';
		options?: {
			includeNodeValidation?: boolean;
			aiEnhancement?: boolean;
		};
	}): Promise<ValidationResult> {
		const response: AxiosResponse<ValidationResult> = await validationApiClient.post(
			'/validation/validate-dag',
			request
		);
		return response.data;
	},

	/**
	 * Get validation system status
	 */
	async getValidationStatus(): Promise<{
		validators: Record<string, { implemented: boolean; phase: number }>;
		aiAvailable: boolean;
		aiStatus: { configured: boolean; model?: string };
	}> {
		const response: AxiosResponse<any> = await validationApiClient.get('/validation/status');
		return response.data;
	},
};

/**
 * Combined API service exports
 */
export const api = {
	flows: flowsApi,
	folders: foldersApi,
	executions: executionsApi,
	forms: formsApi,
	webhooks: webhooksApi,
	validation: validationApi,
};

export default api;
